import {Injectable} from '@angular/core';

import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';

import {
  concatMap, delay,
  EMPTY,
  filter,
  merge,
  mergeMap,
  Observable,
  of,
  retry,
  shareReplay,
  tap,
  throwError,
  timer
} from 'rxjs';
import {LocalStorageService} from 'ngx-localstorage';
import {catchError, finalize, retryWhen} from 'rxjs/operators';
import {AppConfig} from './app.config';
import {JwtAuthenticationResponseToken} from '../api/defs/JwtAuthenticationResponse';
import {environment} from '../environments/environment';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {ElectronService} from './core/services';
import {CacheInterceptorControllerService} from './services/cache.service';
import {httpRetryStrategyPipe} from './shared/http-retry-strategy.pipe';

export interface RetryParams {
  maxAttempts?: number;
  scalingDuration?: number;
  // @ts-ignore
  shouldRetry?: ({status: number}) => boolean;
}

const defaultParams: RetryParams = {
  maxAttempts: 3,
  scalingDuration: 1000,
  shouldRetry: ({status}) => status >= 400
}

export const genericRetryStrategy = (params: RetryParams = {}) => (attempts: Observable<any>) => attempts.pipe(
  mergeMap((error, i) => {
    const {maxAttempts, scalingDuration, shouldRetry} = {...defaultParams, ...params}
    const retryAttempt = i + 1;
    // if maximum number of retries have been met
    // or response is a status code we don't wish to retry, throw error
    if (retryAttempt > maxAttempts || !shouldRetry(error)) {
      return throwError(error);
    }
    console.log(`Attempt ${retryAttempt}: retrying in ${retryAttempt * scalingDuration}ms`);
    // retry after 1s, 2s, etc...
    return timer(retryAttempt * scalingDuration);
  })
);

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {

  count = 0;

  constructor(
    private appConfig: AppConfig,
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
    private controller: CacheInterceptorControllerService,
    private electronService: ElectronService
  ) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {

    let req = request;

    let user: JwtAuthenticationResponseToken = this.localStorageService.get('token')

    if (!request.url.includes('oplawebfluxstreaming')) {

      if (!request.url.includes('drillingparams')) {
        this.appConfig.mainLoading = true;
      }

      if (!request.url.includes('auth')) {
        req = request.clone({
          setHeaders: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + user.access_token
          },
        });
      }
    }

    if (req.url.includes('windowing')) {
      console.log(this.isCanCache(req));
    }

    return next.handle(req)
      .pipe(
        httpRetryStrategyPipe(this.appConfig.httpRetryAttempts),
        catchError((error) => {
          this.appConfig.mainLoading = false;

          if (error.error.message === 'The submitted token was not found') {
            this.refreshToken();
          }

          return throwError(error);
        }),
        shareReplay(),
        finalize(() => this.appConfig.mainLoading = false)
      );
  }

  async refreshToken() {

    const fp = await FingerprintJS.load();
    const result = await fp.get();

    let token: JwtAuthenticationResponseToken = this.localStorageService.get('token');

    let body = {
      'client_id': 'MPDLiveWeb',
      'client_secret': '*',
      'deviceID': result.visitorId,
      'grant_type': 'refresh',
      'refresh_token': token.refresh_token
    };

    try {
      let res = await this.httpClient.post(`${environment.authHost}/opla-oauth-service/oauth/token`, body).toPromise() as any;

      let authResult = res.result?.results;

      this.localStorageService.set('user', authResult.user);
      this.localStorageService.set('token', authResult.token);

      window.location.reload();
    } catch (e) {
      this.localStorageService.remove('user');
      this.localStorageService.remove('token');
      window.location.replace('./');
      console.log(e);
    }

  }

  private isCanCache(req: HttpRequest<any>): boolean {
    const query = req.body as any;
    const disableCacheForGetRequest = req.params.get('canCache') === 'false'
      || req.url.indexOf('canCache=false') > 0
      || req.url.includes('windowing')

    return (req.body && req.method === 'POST' && query.canCache === true)
      || (req.method === 'GET' && !disableCacheForGetRequest);
  }


  private isIgnoreFirstCache(req: HttpRequest<any>): boolean {
    // Модуль локализаций по take(1) прерывает поток, поэтому ему не отдаем первый кеш
    return !!req.url.match(/i18n\/\w{2,5}\.json/);
  }

}
